<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ChatMessage } from '@/types';
import { BaseButton, BaseIcon } from '@/components/Base';
import ImageModal from './ImageModal.vue';
import { EmergencySituationInstructions } from '@/enums/EmergencySituationInstructions';
import InstructionsModal from './InstructionsModal.vue';

type MessageType = 'received' | 'sent' | 'sent-sms';
export type DeliveryStatus = 'delivered' | 'sms-delivered';

const { t } = useI18n();

const showImageModal = ref(false);
const showInstructions = ref(false);

const props = withDefaults(defineProps<{
  message: ChatMessage;
  type?: MessageType;
  translationsAllowed?: boolean;
  deliveryStatus?: DeliveryStatus;
}>(), {
  type: 'received'
});

const hasInstructions = computed(() => {
  return props.message.emergencySituationInstructions !== EmergencySituationInstructions.None;
});

const messageClass = computed(() => [
  'chat-message',
  `chat-message--${props.type}`,
  hasInstructions.value ? 'chat-message--has-instructions' : ''
]);

const formattedTime = computed(() => {
  if (!props.message.sentTimeUtc) return '';

  try {
    const date = new Date(props.message.sentTimeUtc);
    return date.toLocaleTimeString('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
});

const deliveryStatusLabel = computed(() => {
  if (props.type === 'received') return null;

  switch (props.deliveryStatus) {
    case 'delivered':
      return t('chat.deliveryStatus.delivered');
    case 'sms-delivered':
      return t('chat.deliveryStatus.deliveredSms');
    default:
      return null;
  }
});
</script>

<template>
  <div :class="messageClass">

    <div class="chat-message__content">
      <div class="chat-message__header">
        <span class="chat-message__time">{{ formattedTime }}</span>
        <span v-if="deliveryStatusLabel" class="chat-message__status">
          {{ deliveryStatusLabel }}
        </span>
      </div>

      <p class="chat-message__text">{{ message.message }}</p>

      <!-- Instructions button - opens modal -->
      <BaseButton
        size="small"
        v-if="hasInstructions"
        @click="showInstructions = true"
      >
        {{ t('chat.showInstructions') }}
      </BaseButton>

      <div v-if="translationsAllowed && message.translatedMessage && type === 'received'" class="chat-message--translate">
        <p class="text-xs color-sec"><BaseIcon name="translate" size="16"></BaseIcon> {{ t('chat.msgTranslated') }}</p>
        <p class="chat-message__text">{{ message.translatedMessage }}</p>
      </div>

      <img v-if="message.attachedImageUrl"
           :src="message.attachedImageUrl"
           class="chat-message__image"
           :alt="t('chat.imageAlt')"
           @click="showImageModal = true" />
    </div>

    <ImageModal
      v-if="showImageModal && message.attachedImageUrl"
      :src="message.attachedImageUrl"
      :alt="t('chat.imageAlt')"
      @close="showImageModal = false"
    />

    <InstructionsModal
      v-if="showInstructions && hasInstructions"
      :instruction="message.emergencySituationInstructions"
      @close="showInstructions = false"
    />
  </div>
</template>

<style>
.chat-message {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  margin-bottom: var(--spacing-sm);

  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius);
  border: 1px solid transparent;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Received messages - aligned left */
.chat-message--received {
  align-self: flex-start;
}

/* Sent messages - aligned right */
.chat-message--sent,
.chat-message--sent-sms {
  align-self: flex-end;
}

.chat-message__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-xs);
  margin-bottom: 2px;
}

.chat-message__time {
  font-size: var(--font-size-xs);
  color: var(--color-text-label);
}

.chat-message__status {
  font-size: var(--font-size-xs);
  color: var(--color-text-label);
}

.chat-message--translate {
  border-top: var(--border-width) solid var(--color-border);
  margin-top: var(--spacing-xs);
}

/* Received message styling */
.chat-message--received {
  background: linear-gradient(180deg, var(--color-msg-received-bg-start) 0%, var(--color-msg-received-bg-end));
  color: var(--color-msg-text);
  border-bottom-left-radius: 0;
}

/* Sent message styling (internet) */
.chat-message--sent {
  background: linear-gradient(180deg, var(--color-msg-send-bg-start) 0%, var(--color-msg-send-bg-end));
  border-bottom-right-radius: 0;
}

/* Sent SMS styling */
.chat-message--sent-sms .chat-message__content {
  background: linear-gradient(180deg, var(--color-sms-send-bg-start) 0%, var(--color-sms-send-bg-end));
  color: var(--color-text);
  border-top-right-radius: 0;
}

.chat-message__text {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--color-msg-text);
  line-height: 1.4;
}

.chat-message__image {
  max-width: 100%;
  max-height: 200px;
  border-radius: var(--border-radius);
  margin-top: var(--spacing-xs);
  display: block;
  cursor: pointer;
}

.chat-message--has-instructions {
  max-width: 90%;
}
</style>
